// store.js
import { writable, derived } from 'svelte/store';

// Create the main stores for our application
export const discountItems = writable([]);
export const recipes = writable([]);
export const selectedIngredients = writable([]);
export const searchTerm = writable('');
export const selectedCategories = writable([]);

// Mock discount data for the POC
const mockDiscountItems = [
  { 
    id: 1, 
    name: 'Laxfilé hel sida', 
    category: 'seafood', 
    originalPrice: 199.00, 
    discountPrice: 149.00, 
    unit: 'kg', 
    image: 'images/salmon.jpg', 
    discount: 25,
    description: 'Hel sida, ca 1,2 kg, Fångstzon: se förp.' 
  },
  { 
    id: 2, 
    name: 'Kycklingbröstfilé', 
    category: 'meat', 
    originalPrice: 91.90, 
    discountPrice: 69.90, 
    unit: 'kg', 
    image: 'images/chicken.jpg', 
    discount: 23,
    description: 'GYLLDA, 1 kg, Ursprung: Sverige' 
  },
  { 
    id: 3, 
    name: 'Kvisttomater', 
    category: 'vegetables', 
    originalPrice: 39.90, 
    discountPrice: 24.90, 
    unit: 'kg', 
    image: 'images/tomatoes.jpg', 
    discount: 37,
    description: 'Ursprung: Spanien' 
  },
  { 
    id: 4, 
    name: 'OLW snacks', 
    category: 'snacks', 
    originalPrice: 83.70, 
    discountPrice: 59.00, 
    unit: '3-pack', 
    image: 'images/olw.jpg', 
    discount: 30,
    description: '200 g–275 g/förp. Olika sorter. 3 för 59:-' 
  },
  { 
    id: 5, 
    name: 'Gryta', 
    category: 'kitchenware', 
    originalPrice: 299.00, 
    discountPrice: 229.00, 
    unit: 'st', 
    image: 'images/pot.jpg', 
    discount: 23,
    description: 'LIVARNO home®, 2–6 liter, ø 20 cm–ø 28 cm' 
  },
  { 
    id: 6, 
    name: 'Gul lök', 
    category: 'vegetables', 
    originalPrice: 12.90, 
    discountPrice: 9.90, 
    unit: 'kg', 
    image: 'images/onion.jpg', 
    discount: 23,
    description: 'Ursprung: Sverige, Lösvikt' 
  },
  { 
    id: 7, 
    name: 'Röda äpplen', 
    category: 'fruit', 
    originalPrice: 45.00, 
    discountPrice: 39.90, 
    unit: '2.5kg', 
    image: 'images/apples.jpg', 
    discount: 11,
    description: 'Sort: Gala/Crimson Snow, Ursprung: Italien, 15.96 kr/kg' 
  },
  { 
    id: 8, 
    name: 'Färsk kycklingfärs', 
    category: 'meat', 
    originalPrice: 69.90, 
    discountPrice: 59.90, 
    unit: '750g', 
    image: 'images/minced_chicken.jpg', 
    discount: 14,
    description: 'GYLLDA, Ursprung: Sverige, 79.87 kr/kg' 
  },
  { 
    id: 9, 
    name: 'Färsk karré', 
    category: 'meat', 
    originalPrice: 89.90, 
    discountPrice: 79.90, 
    unit: 'kg', 
    image: 'images/pork.jpg', 
    discount: 11,
    description: 'SVEA LANTKÖTT, Av fläsk, Med ben i skivor, Ursprung: Sverige' 
  },
  { 
    id: 10, 
    name: 'Allerum vällagrad ost', 
    category: 'dairy', 
    originalPrice: 69.90, 
    discountPrice: 59.90, 
    unit: '400g', 
    image: 'images/cheese.jpg', 
    discount: 14,
    description: 'Grevé, Herrgård, Präst, Lagrad 12 mån, Ursprung: Sverige' 
  },
  { 
    id: 11, 
    name: 'Pasta', 
    category: 'pantry', 
    originalPrice: 18.90, 
    discountPrice: 12.90, 
    unit: '500g', 
    image: 'images/pasta.jpg', 
    discount: 32,
    description: 'COMBINO, Olika sorter, Ursprung: Italien' 
  },
  { 
    id: 12, 
    name: 'Potatis', 
    category: 'vegetables', 
    originalPrice: 22.90, 
    discountPrice: 19.90, 
    unit: '2kg', 
    image: 'images/potatoes.jpg', 
    discount: 13,
    description: 'Fast, Ursprung: Sverige' 
  }
];

// Mock recipe data for the POC
const mockRecipes = [
  {
    id: 101,
    name: 'Ugnsbakad lax med rostade rotfrukter',
    ingredients: [
      { id: 1, name: 'Laxfilé', amount: '800g' },
      { id: 3, name: 'Kvisttomater', amount: '200g' },
      { id: 6, name: 'Gul lök', amount: '1 st' },
      { id: 12, name: 'Potatis', amount: '500g' }
    ],
    instructions: 'Värm ugnen till 200°C. Lägg laxfilén i en ugnsform, krydda med salt och peppar. Skär rotfrukter i bitar, blanda med olja och kryddor. Lägg grönsakerna runt laxen. Tillaga i ugnen ca 20-25 minuter.',
    image: 'images/salmon_recipe.jpg',
    prepTime: 30,
    difficulty: 'Lätt',
    category: 'Middag',
    nutritionInfo: {
      calories: 450,
      protein: 35,
      carbs: 30,
      fat: 20
    },
    portions: 4
  },
  {
    id: 102,
    name: 'Kycklinggryta med grönsaker',
    ingredients: [
      { id: 2, name: 'Kycklingbröstfilé', amount: '500g' },
      { id: 6, name: 'Gul lök', amount: '2 st' },
      { id: 3, name: 'Kvisttomater', amount: '300g' },
      { id: 11, name: 'Pasta', amount: '400g' }
    ],
    instructions: 'Skär kycklingen i bitar. Fräs löken i en gryta, tillsätt kycklingen och stek tills den får färg. Tillsätt tomater och kryddor. Låt sjuda i ca 15-20 minuter. Servera med pasta.',
    image: 'images/chicken_recipe.jpg',
    prepTime: 45,
    difficulty: 'Medel',
    category: 'Middag',
    nutritionInfo: {
      calories: 520,
      protein: 40,
      carbs: 45,
      fat: 15
    },
    portions: 4
  },
  {
    id: 103,
    name: 'Äppelpaj',
    ingredients: [
      { id: 7, name: 'Röda äpplen', amount: '6 st' }
    ],
    instructions: 'Skala och skär äpplena i bitar. Blanda med socker och kanel. Gör en deg av smör, mjöl och socker. Smula degen över äpplena i en pajform. Grädda i ugnen i 25-30 minuter vid 175°C.',
    image: 'images/apple_pie.jpg',
    prepTime: 40,
    difficulty: 'Lätt',
    category: 'Efterrätt',
    nutritionInfo: {
      calories: 320,
      protein: 5,
      carbs: 50,
      fat: 12
    },
    portions: 6
  },
  {
    id: 104,
    name: 'Köttfärslimpa med karrésås',
    ingredients: [
      { id: 8, name: 'Färsk kycklingfärs', amount: '500g' },
      { id: 9, name: 'Färsk karré', amount: '400g' },
      { id: 6, name: 'Gul lök', amount: '1 st' },
      { id: 12, name: 'Potatis', amount: '800g' }
    ],
    instructions: 'Blanda färs med ägg, ströbröd, lök och kryddor. Forma till en limpa och stek i ugnen vid 200°C ca 40 minuter. Skär karrén i bitar och koka med buljong, lök och kryddor till en god sås. Servera med kokt potatis.',
    image: 'images/meatloaf.jpg',
    prepTime: 60,
    difficulty: 'Medel',
    category: 'Middag',
    nutritionInfo: {
      calories: 650,
      protein: 45,
      carbs: 40,
      fat: 35
    },
    portions: 4
  },
  {
    id: 105,
    name: 'Pastagratäng med ost och tomat',
    ingredients: [
      { id: 11, name: 'Pasta', amount: '400g' },
      { id: 3, name: 'Kvisttomater', amount: '400g' },
      { id: 10, name: 'Allerum vällagrad ost', amount: '200g' },
      { id: 6, name: 'Gul lök', amount: '1 st' }
    ],
    instructions: 'Koka pastan enligt anvisningar. Skär tomaterna i bitar och stek löken. Blanda pasta, tomater och lök i en ugnsform. Riv osten över och gratinera i ugnen vid 200°C i ca 15 minuter tills osten har smält och fått fin färg.',
    image: 'images/pasta_gratin.jpg',
    prepTime: 30,
    difficulty: 'Lätt',
    category: 'Middag',
    nutritionInfo: {
      calories: 480,
      protein: 25,
      carbs: 60,
      fat: 18
    },
    portions: 4
  },
  {
    id: 106,
    name: 'Kycklingwrap med grönsaksfyllning',
    ingredients: [
      { id: 2, name: 'Kycklingbröstfilé', amount: '300g' },
      { id: 3, name: 'Kvisttomater', amount: '200g' },
      { id: 6, name: 'Gul lök', amount: '1 st' }
    ],
    instructions: 'Stek kycklingen och krydda med salt, peppar och paprikapulver. Skär tomater och lök i bitar. Värm tortillabröd och fyll med kyckling och grönsaker. Rulla ihop och servera med sallad.',
    image: 'images/chicken_wrap.jpg',
    prepTime: 20,
    difficulty: 'Lätt',
    category: 'Lunch',
    nutritionInfo: {
      calories: 380,
      protein: 30,
      carbs: 35,
      fat: 12
    },
    portions: 2
  }
];

// Parse discount data from a weekly flyer
export function parseDiscountData(newData = null) {
  // This would normally parse data from an API or file
  // For now, we'll just use the mock data
  
  if (newData) {
    discountItems.set(newData);
  } else {
    discountItems.set(mockDiscountItems);
  }
}

// Fetch recipes that match the selected ingredients
export function fetchMatchingRecipes(newRecipes = null) {
  // This would normally fetch data from an API
  // For now, we'll use mock data
  
  if (newRecipes) {
    recipes.set(newRecipes);
  } else {
    recipes.set(mockRecipes);
  }
}

// Derived store for filtered discount items
export const filteredDiscounts = derived(
  [discountItems, searchTerm, selectedCategories],
  ([$discountItems, $searchTerm, $selectedCategories]) => {
    return $discountItems.filter(item => {
      const matchesSearch = !$searchTerm || 
        item.name.toLowerCase().includes($searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes($searchTerm.toLowerCase());
        
      const matchesCategory = $selectedCategories.length === 0 || 
        $selectedCategories.includes(item.category);
        
      return matchesSearch && matchesCategory;
    });
  }
);

// Derived store for suggested recipes based on selected ingredients
export const suggestedRecipes = derived(
  [recipes, selectedIngredients],
  ([$recipes, $selectedIngredients]) => {
    if ($selectedIngredients.length === 0) {
      return [];
    }
    
    // Get all recipe names to check for name matching
    const recipeIngredientNames = new Set();
    $recipes.forEach(recipe => {
      recipe.ingredients.forEach(ing => {
        recipeIngredientNames.add(ing.name.toLowerCase());
      });
    });
    
    // Get the selected ingredient names
    const selectedIngredientNames = $selectedIngredients.map(item => item.name.toLowerCase());
    
    // Console log for debugging
    console.log('Selected ingredients:', selectedIngredientNames);
    console.log('Recipe ingredient names:', Array.from(recipeIngredientNames));
    
    const matchingRecipes = $recipes.filter(recipe => {
      // Check if the recipe contains at least one selected ingredient
      return recipe.ingredients.some(ingredient => {
        const ingredientNameLower = ingredient.name.toLowerCase();
        return selectedIngredientNames.some(selectedName => {
          // Check for partial matches in both directions
          return ingredientNameLower.includes(selectedName) || 
                 selectedName.includes(ingredientNameLower);
        });
      });
    });
    
    // Sort recipes by how many selected ingredients they contain
    return matchingRecipes.sort((a, b) => {
      const aMatches = a.ingredients.filter(ingredient => {
        const ingredientNameLower = ingredient.name.toLowerCase();
        return selectedIngredientNames.some(selectedName => 
          ingredientNameLower.includes(selectedName) || 
          selectedName.includes(ingredientNameLower)
        );
      }).length;
      
      const bMatches = b.ingredients.filter(ingredient => {
        const ingredientNameLower = ingredient.name.toLowerCase();
        return selectedIngredientNames.some(selectedName => 
          ingredientNameLower.includes(selectedName) || 
          selectedName.includes(ingredientNameLower)
        );
      }).length;
      
      return bMatches - aMatches;
    });
  }
);

// Function to get ingredient match percentage for a recipe
export function getIngredientMatchPercentage(recipe, selectedIngs) {
  if (!recipe || !recipe.ingredients || recipe.ingredients.length === 0) {
    return 0;
  }
  
  const selectedIngredientNames = selectedIngs.map(item => item.name.toLowerCase());
  
  const matchCount = recipe.ingredients.filter(ingredient => {
    const ingredientNameLower = ingredient.name.toLowerCase();
    return selectedIngredientNames.some(selectedName => 
      ingredientNameLower.includes(selectedName) || 
      selectedName.includes(ingredientNameLower)
    );
  }).length;
  
  return Math.round((matchCount / recipe.ingredients.length) * 100);
}

// Helper functions for managing selected ingredients
export function toggleIngredientSelection(item) {
  selectedIngredients.update(ingredients => {
    const index = ingredients.findIndex(i => i.id === item.id);
    if (index === -1) {
      return [...ingredients, item];
    } else {
      return ingredients.filter(i => i.id !== item.id);
    }
  });
}

export function isIngredientSelected(item) {
  let result = false;
  selectedIngredients.subscribe(ingredients => {
    result = ingredients.some(i => i.id === item.id);
  })();
  return result;
}

// Initialize the stores with data
export function initializeStores() {
  parseDiscountData();
  fetchMatchingRecipes();
}

// Add new recipe to the store
export function addRecipe(newRecipe) {
  recipes.update(existingRecipes => {
    // Generate a new ID for the recipe
    const newId = Math.max(...existingRecipes.map(r => r.id), 0) + 1;
    const recipeWithId = { ...newRecipe, id: newId };
    return [...existingRecipes, recipeWithId];
  });
}

// Add new discount item to the store
export function addDiscountItem(newItem) {
  discountItems.update(existingItems => {
    // Generate a new ID for the item
    const newId = Math.max(...existingItems.map(item => item.id), 0) + 1;
    const itemWithId = { ...newItem, id: newId };
    return [...existingItems, itemWithId];
  });
}